import { useQuery } from '@tanstack/react-query';
import { DollarSign, TrendingUp, Users, Receipt, BadgeCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/misc';
import { apiFetch } from '@/lib/api';
import { brl } from '@/lib/money';
import { useT } from '@/lib/i18n';

interface Finance {
  tenants: number; paying_tenants: number; mrr_cents: number;
  revenue_month_cents: number; paid_invoices: number;
}

function Stat({ icon: Icon, label, value, tone, sub }: { icon: LucideIcon; label: string; value: string; tone: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 p-5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">{label}</span>
          <Icon className={`size-4 ${tone}`} />
        </div>
        <p className="text-2xl font-bold tabular-nums">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export function PlatformFinance() {
  const t = useT();
  const { data, isLoading } = useQuery({
    queryKey: ['platform-finance'],
    queryFn: async (): Promise<Finance | null> => {
      const r = await apiFetch('GET', '/platform/finance');
      return r.ok ? (r.data as Finance) : null;
    },
  });

  if (isLoading || !data) {
    return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>;
  }
  const d = data;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">{t('finance.introLead')} <span className="text-amber-600">{t('finance.introAttention')}</span> {t('finance.introTail')}</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Stat icon={TrendingUp} label={t('finance.mrr')} value={brl(d.mrr_cents)} tone="text-emerald-500" sub={t('finance.mrrSub')} />
        <Stat icon={DollarSign} label={t('finance.revenueMonth')} value={brl(d.revenue_month_cents)} tone="text-primary" sub={t('finance.paidInvoicesMonthSub')} />
        <Stat icon={Users} label={t('finance.tenants')} value={d.tenants.toLocaleString('pt-BR')} tone="text-amber-500" />
        <Stat icon={BadgeCheck} label={t('finance.payingTenants')} value={d.paying_tenants.toLocaleString('pt-BR')} tone="text-sky-500" sub={t('finance.payingTenantsSub')} />
        <Stat icon={Receipt} label={t('finance.paidInvoices')} value={d.paid_invoices.toLocaleString('pt-BR')} tone="text-rose-500" sub={t('finance.paidChargesSub')} />
      </div>
    </div>
  );
}
